import {
  Grid,
  Typography,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import { useState } from "react";
import { useTheme } from "@mui/material/styles";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Tooltip from "@mui/material/Tooltip";
import { updateLawsuitJucAndLegalFile } from "../../services/LawsuitService";
import CardHeader from "../General/CardHeader";
import FileUploadBox from "../General/FileUploadBox";
import GlassCard from "../General/GlassCard";
import DynamicExcelHelpModal from "../General/DynamicExcelHelpModal";
import { useToast } from "../../context/ToastContext";

export default function UpdateLawsuitJucLegalFile() {
  const { showToast } = useToast();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [openHelp, setOpenHelp] = useState(false);

  const handleFileSelect = (file) => {
    if (file) {
      const validExtensions = ["xls", "xlsx"];
      const fileExtension = file.name.split(".").pop().toLowerCase();
      if (!validExtensions.includes(fileExtension)) {
        setErrors({ ...errors, file: "Solo se permiten archivos .xls o .xlsx" });
        setSelectedFile(null);
        return;
      }
      setErrors({ ...errors, file: "" });
      setSelectedFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!selectedFile) {
      showToast("Debe seleccionar un archivo.", "info");
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const response = await updateLawsuitJucAndLegalFile(formData);
      showToast(response.data.message, "success");
      setSelectedFile(null);
      setErrors({});
    } catch (error) {
      showToast(
        error.response?.data?.message || "Ocurrió un error inesperado.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 10 }}>
        <GlassCard>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Tooltip title="Ver requisitos del Excel" arrow>
              <HelpOutlineIcon
                sx={{
                  cursor: "pointer",
                  color: theme.palette.primary.main,
                  "&:hover": { color: theme.palette.primary.dark },
                }}
                onClick={() => setOpenHelp(true)}
              />
            </Tooltip>
          </Box>

          <CardHeader
            icon={<BadgeRoundedIcon />}
            title="Actualizar Estado y ID"
            description="Completar el JUC ID y, opcionalmente, el Estado del Sistema de títulos ya existentes, sin importar su estado actual."
          />

          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <FileUploadBox
                  selectedFile={selectedFile}
                  onFileSelect={handleFileSelect}
                  accept=".xls,.xlsx"
                  placeholder="Seleccionar Excel"
                  helperText="O arrastrá y soltá el archivo aquí (.xls, .xlsx)"
                />
                {errors.file && (
                  <Typography
                    color="error"
                    variant="caption"
                    sx={{ mt: 1, display: "block" }}
                  >
                    {errors.file}
                  </Typography>
                )}
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  fullWidth
                  disabled={loading}
                  sx={{ mt: 1 }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Actualizar Estado y ID"
                  )}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </GlassCard>
      </Grid>

      <DynamicExcelHelpModal
        open={openHelp}
        onClose={() => setOpenHelp(false)}
        title="Requisitos del archivo Excel"
        description="El archivo debe ser formato .xls o .xlsx. Columnas: código de título, JUC ID y, opcionalmente, el Código del Estado del Sistema."
        columns={[
          "Código de título (ej. Aut/9736/2023)",
          "JUC ID",
          "Código del Estado del Sistema (opcional, ver Configuración → Estados del Sistema)",
        ]}
        example={["Aut/9736/2023", "JUC-12345", "5"]}
      />
    </Grid>
  );
}
